import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADJUSTMENT_KINDS, ADJUSTMENT_LABEL, type AdjustmentKind } from '@veta-desk/domain';
import { Shell } from '../components/Shell';
import { STATEMENT_STATUS } from '../components/StatementsSection';
import { api, ApiError, type StatementDetail as Detail } from '../lib/api';
import { dateTime } from '../lib/format';
import { MODEL_LABEL, h, monthLabel } from '../lib/contracts';

// Etapa 2.3 · Estado de cuenta de un mes: renglones por ticket, ajustes, aclaraciones del cliente,
// envío y cierre. Lo capturado nunca se toca; lo cargado se corrige con ajustes (admin o líder).

const field = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';
const td = 'px-3 py-2 text-right font-mono tabular-nums';

export default function StatementDetail() {
  const { id = '', month = '' } = useParams();
  const queryClient = useQueryClient();
  const key = ['statement', id, month];
  const q = useQuery({ queryKey: key, queryFn: () => api<Detail>(`/contracts/${id}/statements/${month}`) });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [adj, setAdj] = useState<{ kind: AdjustmentKind; hours: string; ticket: string; warrantyOf: string; reason: string } | null>(null);
  const [resolving, setResolving] = useState<{ id: string; text: string } | null>(null);

  const done = (d: Detail, text: string) => {
    queryClient.setQueryData(key, d);
    void queryClient.invalidateQueries({ queryKey: ['statements', id] });
    void queryClient.invalidateQueries({ queryKey: ['contract', id] });
    setMsg({ ok: true, text });
  };
  const fail = (e: unknown) => setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Algo salió mal.' });
  const post = (path: string, body: unknown = {}) => api<Detail>(`/contracts/${id}/statements/${month}${path}`, { method: 'POST', body: JSON.stringify(body) });
  const send = useMutation({ mutationFn: () => post('/send'), onSuccess: d => done(d, 'Enviado al cliente: los aprobadores recibieron el aviso con la liga al portal.'), onError: fail });
  const close = useMutation({ mutationFn: () => post('/close'), onSuccess: d => done(d, 'Mes cerrado.'), onError: fail });
  const addAdj = useMutation({
    mutationFn: () => post('/adjustments', { kind: adj!.kind, hours: Number(adj!.hours), reason: adj!.reason, ticket: adj!.ticket || null, warrantyOf: adj!.warrantyOf || null }),
    onSuccess: d => { done(d, 'Ajuste registrado.'); setAdj(null); }, onError: fail
  });
  const resolve = useMutation({
    mutationFn: () => post(`/clarifications/${resolving!.id}/resolve`, { resolution: resolving!.text }),
    onSuccess: d => { done(d, 'Aclaración resuelta. Cuando no queden pendientes, reenvía el estado de cuenta.'); setResolving(null); }, onError: fail
  });

  if (q.isLoading) return <Shell><p role="status" className="py-10 text-center text-sm text-muted">Cargando…</p></Shell>;
  if (q.isError || !q.data) return <Shell><p role="alert" className="rounded-lg border border-line bg-white p-8 text-center text-sm">{(q.error as Error)?.message ?? 'No encontramos ese estado de cuenta.'}</p></Shell>;
  const { statement: s, summary, lines, permissions } = q.data;
  const row = summary.baseline?.row;
  const st = STATEMENT_STATUS[s.status];
  const tickets = lines.filter(l => l.ticket).map(l => l.ticket);

  return (
    <Shell>
      <div className="flex flex-col gap-5">
        <Link to={`/contratos/${id}`} className="text-sm text-accent hover:underline print:hidden">← {s.projectCode}</Link>
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono text-muted">{s.projectCode} · {MODEL_LABEL[s.model]}</span>
              <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${st.chip}`}>{st.label}</span>
            </p>
            <h1 className="mt-1 text-2xl font-semibold">Estado de cuenta · {monthLabel(s.month)}</h1>
            <p className="mt-1 text-sm text-muted">
              {s.organizationName}
              {s.sentAt ? ` · enviado ${dateTime(s.sentAt)}${s.sentBy ? ` por ${s.sentBy.name}` : ''}` : ''}
              {s.status === 'sent' && s.dueAt ? ` · se acepta solo el ${dateTime(s.dueAt)}` : ''}
              {s.closedAt ? ` · cerrado ${dateTime(s.closedAt)} (${s.closedVia === 'client' ? `aceptó ${s.acceptedBy?.replace(/\s*\(.*\)$/, '')}` : s.closedVia === 'auto' ? 'por vencimiento' : 'por el equipo'})` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            {permissions.send && <button type="button" className={btnPrimary} disabled={send.isPending} onClick={() => send.mutate()}>{s.status === 'in_clarification' ? 'Reenviar al cliente' : 'Enviar al cliente'}</button>}
            {permissions.close && <button type="button" className={btnSecondary} disabled={close.isPending} onClick={() => close.mutate()}>Cerrar (el cliente aceptó)</button>}
            <button type="button" className={btnSecondary} onClick={() => window.print()}>Imprimir / PDF</button>
          </div>
        </header>
        {msg && <p role={msg.ok ? 'status' : 'alert'} className={`rounded-md px-3 py-2 text-sm print:hidden ${msg.ok ? 'bg-accent-soft text-accent-dark' : 'bg-[#FBEAEA] text-[#8F1D1D]'}`}>{msg.text}</p>}
        {s.status === 'draft' && <p className="rounded-md bg-paper px-3 py-2 text-sm text-muted print:hidden">Borrador: revisa los renglones, registra los ajustes que hagan falta y envíalo al cliente.</p>}

        <section className="grid gap-3 sm:grid-cols-4">
          <Stat label="Horas capturadas" value={h(summary.actual)} sub="reales (solo interno)" />
          <Stat label="Ajustes" value={`${summary.adjustmentsHours > 0 ? '+' : ''}${h(summary.adjustmentsHours)}`} sub={`${summary.adjustments.length} movimiento${summary.adjustments.length === 1 ? '' : 's'}`} />
          <Stat label="Cargadas al contrato" value={h(summary.charged)} sub="lo que ve el cliente" strong />
          {summary.bag && <Stat label="Bolsa a fin de mes" value={h(summary.bag.remaining)} sub={`disponibles de ${h(summary.bag.maxHours)}`} />}
          {row && <Stat label="Saldo del mes" value={h(row.balance)} sub={row.outOfBand ? `${h(row.outOfBand)} fuera de banda` : row.inBand ? `${h(row.inBand)} en banda` : `rollover ${h(row.carryOut)}`} />}
          {s.model === 'on_demand' && <Stat label="A facturar" value={h(summary.charged)} sub="On Demand" />}
        </section>

        <section className="overflow-x-auto rounded-lg border border-line bg-white">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted">
                <th scope="col" className="border-b border-line px-4 py-2.5 text-left font-semibold">Ticket</th>
                <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-semibold">Reales</th>
                <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-semibold">Cargadas</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-muted">Sin horas en el mes.</td></tr>}
              {lines.map(l => (
                <tr key={l.ticket || 'none'} className="border-b border-[#EFEDE9] last:border-0">
                  <td className="px-4 py-2">
                    {l.ticket ? <Link to={`/tickets/${l.ticket}`} className="font-mono text-xs text-accent hover:underline">{l.ticket}</Link> : null}
                    <span className="block">{l.subject}</span>
                    {l.completion > 0 && <span className="text-xs text-muted">incluye {h(l.completion)} para completar lo cotizado</span>}
                  </td>
                  <td className={`${td} text-muted`}>{l.actual}</td>
                  <td className={`${td} font-semibold`}>{l.charged}</td>
                </tr>
              ))}
              {summary.adjustments.map((a, i) => (
                <tr key={`a${i}`} className="border-b border-[#EFEDE9] bg-paper last:border-0">
                  <td className="px-4 py-2">
                    <span className="text-xs font-semibold">{ADJUSTMENT_LABEL[a.kind as AdjustmentKind] ?? a.kind}{a.ticket ? ` · ${a.ticket}` : ''}{a.warrantyOf ? ` (garantía del ${a.warrantyOf})` : ''}</span>
                    <span className="block text-xs text-muted">{a.reason} · {a.approvedBy?.name}</span>
                  </td>
                  <td className={td} />
                  <td className={`${td} font-semibold`}>{['courtesy', 'warranty'].includes(a.kind) ? `−${Math.abs(a.hours)}` : a.kind === 'extra_charge' ? `+${Math.abs(a.hours)}` : a.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {permissions.adjust && (adj ? (
          <section className="flex flex-col gap-3 rounded-lg border border-line bg-white p-4 print:hidden">
            <h2 className="text-sm font-semibold">Nuevo ajuste</h2>
            <div className="grid gap-3 sm:grid-cols-[1.3fr_0.7fr_1fr]">
              <label className="flex flex-col gap-1 text-sm font-medium">Tipo
                <select className={field} value={adj.kind} onChange={e => setAdj({ ...adj, kind: e.target.value as AdjustmentKind })}>
                  {ADJUSTMENT_KINDS.map(k => <option key={k} value={k}>{ADJUSTMENT_LABEL[k]}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium">Horas
                <input className={field} inputMode="decimal" value={adj.hours} onChange={e => setAdj({ ...adj, hours: e.target.value })}
                  placeholder={['reclass', 'prior_period'].includes(adj.kind) ? '−2 o 2' : '2'} />
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium">Ticket {adj.kind === 'warranty' ? '' : '(opcional)'}
                <select className={field} value={adj.ticket} onChange={e => setAdj({ ...adj, ticket: e.target.value })}>
                  <option value="">—</option>
                  {tickets.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            </div>
            {adj.kind === 'warranty' && (
              <label className="flex max-w-sm flex-col gap-1 text-sm font-medium">Cambio original (en garantía)
                <input className={`${field} font-mono`} placeholder="ASX-2026-000123" value={adj.warrantyOf} onChange={e => setAdj({ ...adj, warrantyOf: e.target.value.toUpperCase() })} />
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm font-medium">Motivo
              <input className={field} value={adj.reason} onChange={e => setAdj({ ...adj, reason: e.target.value })} placeholder="Queda en la auditoría; el cliente lo ve en su estado de cuenta" />
            </label>
            <p className="text-xs text-muted">Cortesía y garantía restan horas; cargo extra suma; reclasificación y periodo anterior llevan su signo. Lo capturado en Veta no cambia.</p>
            <div className="flex gap-2">
              <button type="button" className={btnPrimary} disabled={addAdj.isPending || !(Number(adj.hours) !== 0) || adj.reason.trim().length < 10} onClick={() => addAdj.mutate()}>Registrar ajuste</button>
              <button type="button" className={btnSecondary} onClick={() => setAdj(null)}>Cancelar</button>
            </div>
          </section>
        ) : (
          <div className="print:hidden"><button type="button" className={btnSecondary} onClick={() => setAdj({ kind: 'courtesy', hours: '', ticket: '', warrantyOf: '', reason: '' })}>+ Agregar ajuste</button></div>
        ))}

        {s.clarifications.length > 0 && (
          <section className="rounded-lg border border-line bg-white p-4 print:hidden">
            <h2 className="text-sm font-semibold">Aclaraciones del cliente</h2>
            <ul className="mt-2 flex flex-col gap-3 text-sm">
              {s.clarifications.map(c => (
                <li key={c.id} className={`rounded-md border px-3 py-2.5 ${c.resolvedAt ? 'border-[#EFEDE9]' : 'border-[#F2C9C9] bg-[#FDF3F3]'}`}>
                  <p className="text-xs text-muted">{c.by.replace(/\s*\(.*\)$/, '')} · {dateTime(c.at)}{c.ticket ? ` · ${c.ticket}` : ''}</p>
                  <p className="mt-1">“{c.comment}”</p>
                  {c.resolvedAt ? (
                    <p className="mt-1.5 text-xs text-accent-dark">Respuesta de {c.resolvedBy?.name}: {c.resolution}</p>
                  ) : resolving?.id === c.id ? (
                    <div className="mt-2 flex flex-col gap-2">
                      <input className={field} autoFocus placeholder="Respuesta para el cliente (si hubo ajuste, regístralo arriba)" value={resolving.text} onChange={e => setResolving({ id: c.id, text: e.target.value })} />
                      <div className="flex gap-2">
                        <button type="button" className={btnPrimary} disabled={resolve.isPending || resolving.text.trim().length < 5} onClick={() => resolve.mutate()}>Resolver</button>
                        <button type="button" className={btnSecondary} onClick={() => setResolving(null)}>Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="mt-1.5 text-xs text-accent underline" onClick={() => setResolving({ id: c.id, text: '' })}>Responder</button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Shell>
  );
}

function Stat({ label, value, sub, strong }: { label: string; value: string; sub?: string; strong?: boolean }) {
  return (
    <div className={`rounded-lg border bg-white p-4 ${strong ? 'border-accent' : 'border-line'}`}>
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-1 font-mono text-2xl font-semibold tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </div>
  );
}
